/**
 * Single sidebar link. Every sidebar entry goes through this component so a
 * page blocked for the user's organisation (`dashboard_org_pages`, effect =
 * block) disappears from the nav as well as from the router — the catalogue
 * generator (`scripts/access/genPageCatalogue.mjs`) fails the build if a raw
 * `<a onClick={() => handleNavigation(...)}>` sneaks back in.
 */
import type React from 'react'
import type { ReactNode } from 'react'
import type { IconType } from 'react-icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthUserData, useUpdateAuthUserData } from '../../../authUserProvider'
import { usePageAccess } from '../../../access/pageAccess'
import styles from '../../../globals/sidebar.module.scss'

/** Active-state rules shared by the links and the <details open> groups. */
export const isSidebarPathActive = (path: string, pathname: string): boolean =>
    pathname === path ||
    (path === '/srds-home' && pathname.startsWith('/srds/')) ||
    (path === '/viewthru-batch' && pathname.startsWith('/viewthru-batch/')) ||
    (path === '/comms' && pathname.startsWith('/comms')) ||
    (path === '/military-hearing-loss/leads' && pathname.startsWith('/military-hearing-loss/client/'))

/** Navigate + collapse the mobile sidebar, and the active-path predicate. */
export const useSidebarNavigation = () => {
    const authUser = useAuthUserData()
    const updateAuthUser = useUpdateAuthUserData()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const handleNavigation = (path: string) => {
        if (authUser?.sidebar) updateAuthUser({ ...authUser, sidebar: false })
        navigate(path)
    }
    const isActive = (path: string) => isSidebarPathActive(path, pathname)
    return { handleNavigation, isActive }
}

export interface NavItemProps {
    path: string
    label: ReactNode
    Icon?: IconType
    /** Extra paths that should also light this item up (tabs, detail pages). */
    activePaths?: string[]
    /** Rendered after the label (e.g. a count badge). */
    badge?: ReactNode
    title?: string
}

export const NavItem = ({ path, label, Icon, activePaths = [], badge, title }: NavItemProps) => {
    const { handleNavigation, isActive } = useSidebarNavigation()
    const { isBlocked } = usePageAccess()
    if (isBlocked(path)) return null
    const active = isActive(path) || activePaths.some(isActive)
    // Real href so the link is keyboard-focusable and cmd/ctrl-click opens a
    // new tab; a plain click stays in the SPA.
    const onClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
        e.preventDefault()
        handleNavigation(path)
    }
    return (
        <li className={active ? styles.active : ''}>
            <a href={path} onClick={onClick} title={title}>
                {Icon && <Icon />} {label}
                {badge}
            </a>
        </li>
    )
}

export default NavItem
